import { FivetranServiceEnum, posthogSchema } from "@repo/fivetran/types";
import { Icon } from "@repo/ui/components/icon";
import { Button } from "@repo/ui/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { Field, FieldError, FieldLabel } from "@repo/ui/ui/field";

import {
  DestinationField,
  DestinationSetupField,
  SchemaField,
} from "./connection-fields";
import { ConnectionFormShell } from "./connection-form-shell";
import {
  type ConnectionFormProps,
  useConnectionForm,
} from "./use-connection-form";

export const PosthogFormPage = ({
  onConnected,
  onCancel,
}: ConnectionFormProps = {}) => {
  const { form, embedded, formId } = useConnectionForm({
    service: FivetranServiceEnum.enum.posthog,
    schema: posthogSchema,
    defaultConfig: {
      schema: FivetranServiceEnum.enum.posthog,
      sub_domain: "app",
      api_key: "",
    },
    onConnected,
  });

  return (
    <ConnectionFormShell
      form={form}
      formId={formId}
      icon="posthog"
      iconClassName=""
      submitLabel="Create"
      embedded={embedded}
      onCancel={onCancel}
    >
      <DestinationField form={form} />
      <DestinationSetupField form={form} />

      <div className="flex gap-4">
        <SchemaField form={form} />
        <form.AppField name="config.sub_domain">
          {(field) => {
            const value = field.state.value as string;
            const isInvalid =
              field.state.meta.isTouched && !field.state.meta.isValid;
            const label = value === "eu" ? "EU" : value === "app" ? "US" : null;
            return (
              <Field data-invalid={isInvalid} className="flex-1">
                <FieldLabel htmlFor={field.name}>Domain</FieldLabel>
                <DropdownMenu
                  modal={false}
                  onOpenChange={(open) => {
                    if (!open) field.handleBlur();
                  }}
                >
                  <DropdownMenuTrigger
                    render={
                      <Button
                        id={field.name}
                        variant="outline"
                        className="w-full justify-between font-normal"
                        aria-invalid={isInvalid}
                      />
                    }
                  >
                    {label ? (
                      <span className="text-foreground">{label}</span>
                    ) : (
                      <span className="text-muted-foreground">
                        Select sub domain
                      </span>
                    )}
                    <Icon
                      name="chevron-down"
                      className="size-4 text-muted-foreground"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="bottom">
                    <DropdownMenuRadioGroup
                      value={value}
                      onValueChange={(next) => field.handleChange(next ?? "")}
                    >
                      <DropdownMenuRadioItem value="app">
                        US
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="eu">
                        EU
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                {isInvalid && <FieldError errors={field.state.meta.errors} />}
              </Field>
            );
          }}
        </form.AppField>
      </div>

      <form.AppField name="config.api_key">
        {(field) => (
          <field.PasswordField
            label="API Key"
            placeholder="phc_XXXXXXXXXXXXXXXXXXXXXXXX"
          />
        )}
      </form.AppField>
    </ConnectionFormShell>
  );
};
